import * as React from 'react'
import { graphql } from 'gatsby'
import Layout from '../components/layout'
import Seo from '../components/seo'
import {
  Container, Paper, Typography, Box, Breadcrumbs, Link as MuiLink, Button, Stack, Divider
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

// 👉 make sure this file exists (your fallback styles for WP blocks/buttons/columns)
import '../styles/wp-fallbacks.css'

const WpPageTemplate = ({ data }) => {
  const page = data.wpPage

  return (
    <Layout pageTitle={page.title}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Breadcrumbs + Back */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}
        >
          <Breadcrumbs>
            <MuiLink href="/" underline="hover" color="inherit">Home</MuiLink>
            <Typography color="text.primary">{page.title}</Typography>
          </Breadcrumbs>
          <Button
            href="/"
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
          >
            Back to Home
          </Button>
        </Stack>

        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3 }}>
          <Typography
            variant="h1"
            sx={{ 
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                background: 'linear-gradient(45deg, #0069EA 20%,rgb(115, 153, 194) 80%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
          >
            {page.title}
          </Typography>


          <Divider sx={{ my: 3 }} />

          {/* Content wrapper: your wp-fallback.css handles Gutenberg blocks etc */}
          <Box
            className="wp-fallback" // optional scoping class if your CSS targets it
            sx={{
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                mt: 4, mb: 2, fontWeight: 700, color: 'text.primary'
              },
              '& p': { mb: 2, lineHeight: 1.75, fontSize: '1.1rem' },
              '& ul, & ol': { mb: 2, pl: 3 },
              '& li': { mb: 1, lineHeight: 1.6 },
              '& blockquote': {
                borderLeft: '4px solid', borderColor: 'primary.main',
                pl: 3, py: 2, my: 3, bgcolor: 'grey.50', fontStyle: 'italic',
                borderRadius: '0 8px 8px 0'
              },
              '& code': {
                bgcolor: 'grey.100', px: 1, py: 0.5, borderRadius: 1,
                fontFamily: 'monospace', fontSize: '0.9em'
              },
              '& pre': {
                bgcolor: 'grey.900', color: 'white', p: 3, borderRadius: 2,
                overflow: 'auto', mb: 3
              },
              '& img': { maxWidth: '100%', height: 'auto', borderRadius: 2, my: 3 },
              '& iframe': { width: '100%', minHeight: 400, borderRadius: 2, my: 3 },
              '& table': { width: '100%', borderCollapse: 'collapse', my: 3 },
              '& th, & td': { border: '1px solid rgba(0,0,0,0.12)', p: 1 },
              '& a': {
                color: 'primary.main', textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              },
            }}
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        </Paper>
      </Container>
    </Layout>
  )
}

export const query = graphql`
  query WpPageById($id: String!) {
    wpPage(id: { eq: $id }) {
      id
      title
      uri
      content
      seo {
        title
        metaDesc
        canonical
        opengraphTitle
        opengraphDescription
        opengraphImage { mediaItemUrl }
        metaRobotsNoindex
      }
    }
  }
`

export const Head = ({ data }) => (
  <Seo
    title={data.wpPage.title}
    description={data.wpPage.excerpt?.replace(/<[^>]*>/g, '')}
    yoast={data.wpPage.seo}
    pathname={data.wpPage.uri}
  />
)

export default WpPageTemplate
